import React from 'react'
import { Link } from 'react-router-dom'
import { Table, Tag, Popover, Popconfirm, Space, Button, message } from 'antd'
import { EditOutlined, DeleteOutlined } from '@ant-design/icons'
import { ListComponent, BaseListComponentProps } from '../BaseComponent'
import { SearchRequest, ShelterProductDeleteRequest } from '../../services/api/interfaces/ApiRequest'
import IShelterProduct from '../../interfaces/IShelterProduct'

type ShelterProductListProps = BaseListComponentProps & {
  shelterUuid: string,
  loading: boolean,
  products: IShelterProduct[] | null,
  pagination: any,

  history: any,

  loadProducts: (args: SearchRequest) => void,
  deleteProduct: (args: ShelterProductDeleteRequest) => Promise<any>
}

export default class ShelterProductList extends ListComponent<ShelterProductListProps> {

  static defaultProps = {
    loading: false,
  }

  // Table for branches popover
  branchesTableColumns = [
    {
      title: 'Name',
      dataIndex: ['branch', 'name'],
      key: 'name'
    },
    {
      title: 'Price',
      dataIndex: 'price',
      key: 'price',
      render: (text: string, record: any) => (
        <Tag color="green">{Number(text).toFixed(2)}</Tag>
      )
    },
    {
      title: 'Required',
      dataIndex: 'required',
      key: 'required',
      render: (text: string, record: any) => (
        <Tag color={record.required ? 'cyan' : 'purple'}>{record.required ? 'Yes' : 'No'}</Tag>
      )
    }
  ]

  // Table for species popover
  speciesTableColumns = [
    {
      title: 'Name',
      dataIndex: ['specie', 'name'],
      key: 'name'
    },
    {
      title: 'Required',
      dataIndex: 'required',
      key: 'required',
      render: (text: string, record: any) => (
        <Tag color={record.required ? 'cyan' : 'purple'}>{record.required ? 'Yes' : 'No'}</Tag>
      )
    }
  ]

  tableColumns = [
    {
      title: 'UUID',
      dataIndex: 'uuid',
      key: 'uuid',
      responsive: ['md'] as any,
      sorter: (a: any, b: any) => a.uuid - b.uuid
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a: any, b: any) => a.name - b.name
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      responsive: ['md'] as any,
      sorter: (a: any, b: any) => a.description - b.description
    },
    {
      title: 'Locations',
      dataIndex: 'product_branch',
      key: 'product_branch',
      responsive: ['md'] as any,
      render: (text: string, record: any) => {
        const dataSource = record.product_branch || []

        const popoverContent = (
          <div style={{minHeight: 200, minWidth: 400, overflow: 'auto'}}>
            <Table dataSource={dataSource} columns={this.branchesTableColumns} />
          </div>
        )

        return (
          <Popover content={popoverContent} title="Locations" trigger="hover" placement="bottom">
            <Tag color="geekblue">{record.product_branch?.length || 0}</Tag>
          </Popover>
        )
      }
    },
    {
      title: 'Species',
      dataIndex: 'product_specie',
      key: 'product_specie',
      responsive: ['md'] as any,
      render: (text: string, record: any) => {
        const dataSource = record.product_specie || []

        const popoverContent = (
          <div style={{minHeight: 200, minWidth: 400, overflow: 'auto'}}>
            <Table dataSource={dataSource} columns={this.speciesTableColumns} />
          </div>
        )

        return (
          <Popover content={popoverContent} title="Species" trigger="hover" placement="bottom">
            <Tag color="volcano">{record.product_specie?.length || 0}</Tag>
          </Popover>
        )
      }
    },
    {
      title: 'Price',
      dataIndex: 'price',
      key: 'price',
      responsive: ['md'] as any,
      sorter: (a: any, b: any) => a.price - b.price,
      render: (text: string, record: any) => (
        <Tag color="green">{Number(text).toFixed(2)}</Tag>
      )
    },
    {
      title: 'Type',
      dataIndex: 'type_key',
      key: 'type_key',
      responsive: ['md'] as any,
      sorter: (a: any, b: any) => a.type_key - b.type_key
    },
    {
      title: 'Actions',
      key: 'action',
      render: (text: string, record: any) => (
        <Space size='middle'>
          <Link to={`/organization/shelter/${this.props.shelterUuid}/product/${record.uuid}`} ref={(refs) => this.actionRefs.edit.push(refs)}>
            <Button type="dashed" shape="round" icon={<EditOutlined />} />
          </Link>

          <div ref={(refs) => this.actionRefs.delete.push(refs)}>
            <Popconfirm title="Are you sure about deleting this product?" placement="topLeft" onConfirm={() => this.handleDelete(record.uuid)}>
              <Button type="dashed" shape="round" icon={<DeleteOutlined />} />
            </Popconfirm>
          </div>
        </Space>
      )
    }
  ]

  componentDidMount() {
    this.searchOptions.search = {
      shelter: {
        has: {
          uuid: this.props.shelterUuid
        }
      },
      'product_branch': {
        with: {
          deleted: false,
          branch: {
            with: true
          }
        }
      },
      'product_specie': {
        with: {
          deleted: false,
          specie: {
            with: true
          }
        }
      }
    }

    this.props.loadProducts(this.searchOptions)
  }

  handleDelete = async (uuid: string) => {
    const { deleteProduct } = this.props

    await deleteProduct({
      organization_product_uuid: uuid
    })

    message.success('Product deleted successfully!')
  }

  render() {
    const { loading, pagination, products, loadProducts } = this.props

    let paginationOptions = {}
    if (pagination) {
      paginationOptions = {
        total: pagination.count,
        pageSize: pagination.limit,
        current: pagination.page
      }
    }

    return (
      <>
        <Table
          loading={loading}
          rowKey='uuid'
          columns={this.tableColumns}
          pagination={paginationOptions}
          dataSource={products as any}
          onChange={(pagination: any, filters: any, sorter: any) => this.handleTableChange(pagination, sorter, loadProducts)}
        />
      </>
    )
  }
}
